export interface ParsedEmailList {
  /** Addresses that parsed and validated, in the order they appeared */
  valid: string[];
  /** Entries that could not be resolved to a usable address */
  invalid: string[];
  /** Valid addresses dropped because they repeat an existing or earlier address */
  duplicates: string[];
  /** Display names found alongside a valid address, keyed by lowercased address */
  names: Record<string, string>;
}

// Zero width characters are not matched by \s, so they have to be removed
// before tokenizing or they end up inside the local part.
const ZERO_WIDTH = /[\u200B-\u200D\uFEFF]/g;
const UNICODE_SPACES = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;
const SMART_DOUBLE_QUOTES = /[\u201C\u201D]/g;
const SMART_SINGLE_QUOTES = /[\u2018\u2019]/g;

const ANGLE_BRACKETED = /<([^<>]*)>/g;
const MAILTO_PREFIX = /^mailto:/i;
const LEADING_PUNCTUATION = /^[\s"'([<]+/;
const TRAILING_PUNCTUATION = /[\s"')\]>.,;:]+$/;
const SURROUNDING_QUOTES = /^[\s"']+|[\s"']+$/g;

const EMAIL_ADDRESS =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

const MAX_LOCAL_PART_LENGTH = 64;
const MAX_ADDRESS_LENGTH = 254;

export const isValidEmailAddress = (value: string) => {
  const address = value.trim();
  if (address.length === 0 || address.length > MAX_ADDRESS_LENGTH) return false;
  if (address.split('@')[0].length > MAX_LOCAL_PART_LENGTH) return false;
  return EMAIL_ADDRESS.test(address);
};

const normalize = (input: string) =>
  input
    .replace(ZERO_WIDTH, '')
    .replace(UNICODE_SPACES, ' ')
    .replace(SMART_DOUBLE_QUOTES, '"')
    .replace(SMART_SINGLE_QUOTES, "'");

/**
 * Splits on commas, semicolons, tabs and newlines, but only outside of quotes
 * and angle brackets so that `"Smith, Jane" <jane@acme.com>` stays intact.
 */
const splitEntries = (input: string) => {
  const entries: string[] = [];
  let current = '';
  let inQuotes = false;
  let inAngleBrackets = false;

  for (const character of input) {
    if (character === '"') {
      inQuotes = !inQuotes;
    } else if (character === '<') {
      inAngleBrackets = true;
    } else if (character === '>') {
      inAngleBrackets = false;
    } else if (
      !inQuotes &&
      !inAngleBrackets &&
      (character === ',' ||
        character === ';' ||
        character === '\n' ||
        character === '\r' ||
        character === '\t')
    ) {
      entries.push(current);
      current = '';
      continue;
    }
    current += character;
  }
  entries.push(current);

  return entries.map(entry => entry.trim()).filter(entry => entry.length > 0);
};

/**
 * Pulls the address portion out of a single entry. Angle brackets win, since a
 * display name can itself contain an @. Without brackets, an entry may still
 * hold several space separated addresses.
 */
const getCandidates = (entry: string) => {
  const bracketed = [...entry.matchAll(ANGLE_BRACKETED)].map(match => match[1]);
  if (bracketed.length > 0) return bracketed;

  const unclosedBracket = entry.lastIndexOf('<');
  if (unclosedBracket !== -1) return [entry.slice(unclosedBracket + 1)];

  const addressTokens = entry.split(/\s+/).filter(token => token.includes('@'));

  return addressTokens.length > 0 ? addressTokens : [entry];
};

/**
 * Reads the display name that precedes an address, so `Jane Doe <jane@acme.com>`
 * can prefill a name. Surname first names are only recoverable when the client
 * quoted them, since an unquoted comma is a genuine delimiter.
 */
const getDisplayName = (entry: string) => {
  const addressStart = entry.indexOf('<');
  if (addressStart <= 0) return '';

  const name = entry
    .slice(0, addressStart)
    .replace(SURROUNDING_QUOTES, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Outlook falls back to repeating the address when a contact has no name
  if (name.length === 0 || name.includes('@')) return '';

  const [last, first, ...rest] = name.split(',').map(part => part.trim());
  if (rest.length === 0 && last && first) return `${first} ${last}`;

  return name;
};

const clean = (candidate: string) =>
  candidate
    .trim()
    .replace(LEADING_PUNCTUATION, '')
    .replace(TRAILING_PUNCTUATION, '')
    .replace(MAILTO_PREFIX, '')
    .replace(LEADING_PUNCTUATION, '')
    .replace(TRAILING_PUNCTUATION, '');

/**
 * Parses pasted or typed email input into valid, invalid and duplicate buckets
 * so callers can report what was dropped instead of silently filtering.
 *
 * Handles the mailbox formats mail clients actually produce, including Outlook's
 * `Name <address>; Name <address>` and semicolon separated lists. Casing of
 * valid addresses is preserved, while duplicate detection is case insensitive.
 */
export const parseEmailList = (
  input: string,
  existingEmails: string[] = [],
): ParsedEmailList => {
  const valid: string[] = [];
  const invalid: string[] = [];
  const duplicates: string[] = [];
  const names: Record<string, string> = {};

  const seen = new Set(existingEmails.map(email => email.trim().toLowerCase()));
  const reported = new Set<string>();

  splitEntries(normalize(input)).forEach(entry => {
    const displayName = getDisplayName(entry);

    getCandidates(entry).forEach(candidate => {
      const address = clean(candidate) || entry;

      if (!isValidEmailAddress(address)) {
        const key = address.toLowerCase();
        if (!reported.has(key)) {
          reported.add(key);
          invalid.push(address);
        }
        return;
      }

      const key = address.toLowerCase();
      if (seen.has(key)) {
        if (!reported.has(key)) {
          reported.add(key);
          duplicates.push(address);
        }
        return;
      }

      seen.add(key);
      valid.push(address);
      if (displayName) names[key] = displayName;
    });
  });

  return { valid, invalid, duplicates, names };
};

// Long lists of rejected entries are unreadable in an alert, so only the first
// few are named
const MAX_LISTED_ENTRIES = 30;

const formatEntries = (entries: string[]) => {
  const listed = entries.slice(0, MAX_LISTED_ENTRIES).join(', ');
  const remaining = entries.length - MAX_LISTED_ENTRIES;

  return remaining > 0 ? `${listed} and ${remaining} more` : listed;
};

/**
 * Builds a message describing what a parse dropped. Returns an empty string
 * when everything was added, since success needs no explanation.
 */
export const getEmailListMessage = ({
  valid,
  invalid,
  duplicates,
}: Omit<ParsedEmailList, 'names'>) => {
  if (invalid.length === 0 && duplicates.length === 0) return '';

  const added =
    valid.length > 0
      ? `Added ${valid.length} ${valid.length === 1 ? 'address' : 'addresses'}.`
      : '';
  const unreadable =
    invalid.length > 0 ? `Couldn't read: ${formatEntries(invalid)}.` : '';
  const alreadyAdded =
    duplicates.length > 0 ? `Already added: ${formatEntries(duplicates)}.` : '';

  return [added, unreadable, alreadyAdded].filter(Boolean).join(' ');
};
